import React, { useEffect, useState, useRef } from "react";
import "../../../assets/css/Text.css";
import "../../../assets/css/InputText.css";
import InputLabel from "./inputLabel";
import ChevronDown from "../Icons/ChevronDown";
import Close from "../Icons/Close";
import { TrashCan } from "../Icons/TrashCan";
import { useStaffMode } from "../../../hooks/StaffModeProvider";

interface TimeInputProps {
  title: string;
  errorMsj?: string;
  value?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  onChange: (time24: string) => void;
  onClear?: () => void;
  tooltip?: string;
  minuteInterval?: 1 | 5 | 10 | 15;
}

const convert24To12 = (
  time24: string
): { hour: number; minute: number; period: "AM" | "PM" } | null => {
  if (!time24) {
    return null;
  }
  const [hours, minutes] = time24.split(":");
  if (!hours || !minutes) {
    return null;
  }

  const hour24 = parseInt(hours, 10);
  const minute = parseInt(minutes, 10);

  let hour12 = hour24 % 12;
  if (hour12 === 0) {
    hour12 = 12;
  }
  const period: "AM" | "PM" = hour24 < 12 ? "AM" : "PM";

  return { hour: hour12, minute, period };
};

const convert12To24 = (
  hour12: number,
  minute: number,
  period: "AM" | "PM"
): string => {
  let hour24 = hour12;
  if (period === "PM" && hour12 !== 12) {
    hour24 = hour12 + 12;
  } else if (period === "AM" && hour12 === 12) {
    hour24 = 0;
  }
  return `${hour24.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
};

const format12HourDisplay = (time24: string): string => {
  const converted = convert24To12(time24);
  if (!converted) {
    return "";
  }
  return `${converted.hour}:${converted.minute.toString().padStart(2, "0")} ${converted.period}`;
};

const TimeInput: React.FC<TimeInputProps> = (props) => {
  const [focus, setFocus] = useState<boolean>(false);
  const [selectedHour, setSelectedHour] = useState<number>(12);
  const [selectedMinute, setSelectedMinute] = useState<number>(0);
  const [selectedPeriod, setSelectedPeriod] = useState<"AM" | "PM">("AM");
  const [displayValue, setDisplayValue] = useState<string>("");
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [wasFocused, setWasFocused] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { isStaffMode } = useStaffMode();
  const color = isStaffMode ? "secondary" : "primary";

  const hours = React.useMemo(() => Array.from({ length: 12 }, (_, i) => i + 1), []);
  
  const minuteInterval: number = props.minuteInterval || 1;
  const minutes = React.useMemo(
    () => Array.from(
      { length: Math.floor(60 / minuteInterval) },
      (_, i) => i * minuteInterval
    ),
    [minuteInterval]
  );

  const roundToInterval = (minute: number, interval: number): number => {
    return Math.round(minute / interval) * interval;
  };

  useEffect(() => {
    const interval = props.minuteInterval || 1;
    if (props.value && props.value !== "") {
      const converted = convert24To12(props.value);
      if (converted) {
        const roundedMinute = roundToInterval(converted.minute, interval);
        const adjustedTime24 = convert12To24(converted.hour, roundedMinute, converted.period);
        
        setSelectedHour(converted.hour);
        setSelectedMinute(roundedMinute);
        setSelectedPeriod(converted.period);
        setDisplayValue(format12HourDisplay(adjustedTime24));
        
        if (roundedMinute !== converted.minute) {
          props.onChange(adjustedTime24);
        }
      } else {
        setDisplayValue("");
        setSelectedHour(12);
        setSelectedMinute(0);
        setSelectedPeriod("AM");
      }
    } else {
      setDisplayValue("");
      setSelectedHour(12);
      setSelectedMinute(0);
      setSelectedPeriod("AM");
    }
    setIsInitialized(true);
  }, [props.value, props.minuteInterval]);

  useEffect(() => {
    if (focus) {
      setWasFocused(true);
    }
  }, [focus]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setFocus(false);
      }
    };

    if (focus) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [focus]);

  useEffect(() => {
    if (!isInitialized || !focus) {
      return;
    }
    
    const time24 = convert12To24(selectedHour, selectedMinute, selectedPeriod);
    setDisplayValue(format12HourDisplay(time24));
  }, [selectedHour, selectedMinute, selectedPeriod, isInitialized, focus]);

  useEffect(() => {
    if (isInitialized && wasFocused && !focus) {
      const time24 = convert12To24(selectedHour, selectedMinute, selectedPeriod);
      if (time24) {
        props.onChange(time24);
      }
      setWasFocused(false);
    }
  }, [focus, isInitialized, wasFocused, selectedHour, selectedMinute, selectedPeriod]);

  const handleApply = () => {
    const time24 = convert12To24(selectedHour, selectedMinute, selectedPeriod);
    props.onChange(time24);
    setFocus(false);
  };

  const handleClear = () => {
    setSelectedHour(12);
    setSelectedMinute(0);
    setSelectedPeriod("AM");
    setDisplayValue("");
    props.onChange("");
    setFocus(false);
    props.onClear?.();
  };

  return (
    <div
      className={`flex flex-col gap-2 md:gap-[.625rem] self-stretch items-start w-full relative`}
      ref={dropdownRef}
    >
      <div className="flex w-full">
        <InputLabel
          name="timeInput"
          title={props.title}
          required={props.required}
          tooltip={props.tooltip}
        />
      </div>
      <div
        className={`relative w-full bg-gray-25 ${props.error && "errorInput"}`}
      >
        <input
          name="timeInput"
          id="timeInput"
          value={displayValue}
          placeholder={props.placeholder || "HH:MM AM/PM"}
          onClick={() => !props.disabled && setFocus(!focus)}
          readOnly
          disabled={props.disabled}
          className={`py-[.375rem] md:py-3 px-2 md:px-4 w-full bg-gray-25 border border-solid border-gray-25 outline-none disabled:outline disabled:border-none disabled:outline-offset-0 disabled:outline-gray-300 disabled:text-gray-400 text-base md:text-lg ${props.disabled ? '' : props.error ? 'text-primary' : 'text-input-gray'}`}
        />
        <button
          type="button"
          onClick={() => !props.disabled && setFocus(!focus)}
          disabled={props.disabled}
          className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-auto"
        >
          <div
            className={`${props.disabled ? 'text-gray-200' : 'text-input-gray'} w-5 h-5 md:w-6 md:h-6 ${focus && "rotate-180 duration-75"} ${props.disabled ? "fill-gray-300" : "fill-gray-font"}`}
          >
            <ChevronDown />
          </div>
        </button>
      </div>

      {focus && !props.disabled && (
        <div className="absolute right-0 top-20 bg-neutral z-30 shadow-lg rounded-md border border-gray-200">
          <div className="p-4">
            <div className="flex gap-4 mb-4">
              {/* Selector de Horas */}
              <div className="flex-1">
                <label className="text-base font-medium text-gray-font mb-2 block">
                  Hour
                </label>
                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md">
                  {hours.map((hour) => (
                    <button
                      key={hour}
                      type="button"
                      onClick={() => setSelectedHour(hour)}
                      className={`w-full px-3 py-2 text-left ${
                        selectedHour === hour
                          ? `bg-${color} text-white hover:bg-${color}`
                          : "text-gray-font hover:bg-gray-100"
                      }`}
                    >
                      {hour}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex-1">
                <label className="text-base font-medium text-gray-font mb-2 block">
                  Minute
                </label>
                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md">
                  {minutes.map((minute) => (
                    <button
                      key={minute}
                      type="button"
                      onClick={() => setSelectedMinute(minute)}
                      className={`w-full px-3 py-2 text-left ${
                        selectedMinute === minute
                          ? `bg-${color} text-white hover:bg-${color}`
                          : "text-gray-font hover:bg-gray-100"
                      }`}
                    >
                      {minute.toString().padStart(2, "0")}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex-1">
                <label className="text-base font-medium text-gray-font mb-2 block">
                  Period
                </label>
                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md">
                  {(["AM", "PM"] as const).map((period) => (
                    <button
                      key={period}
                      type="button"
                      onClick={() => setSelectedPeriod(period)}
                      className={`w-full px-3 py-2 text-left ${
                        selectedPeriod === period
                          ? `bg-${color} text-white hover:bg-${color}`
                          : "text-gray-font hover:bg-gray-100"
                      }`}
                    >
                      {period}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex flex-row items-center justify-end gap-2">
              {props.onClear && (
                <button
                  onClick={handleClear}
                  className="text-gray-font w-6 h-6 flex items-center justify-center hover:text-primary transition-colors"
                  type="button"
                >
                  <TrashCan />
                </button>
              )}
              <button
                onClick={handleApply}
                className="text-primary text-lg font-medium font-decimal flex-1 justify-center"
                type="button"
              >
                APPLY
              </button>
            </div>
          </div>
        </div>
      )}

      <span
        className={`flex flex-wrap items-center ${!props.error && "hidden"} text-sm font-medium text-primary`}
      >
        {props.errorMsj}
      </span>
    </div>
  );
};

export default TimeInput;
